import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from "lucide-react";
import { springs } from "@/lib/motion";

const ToastContext = createContext();
const MAX = 3;
const CONFIG = {
  success: { color: "var(--success)", Icon: CheckCircle2, duration: 3000 },
  error: { color: "var(--error)", Icon: XCircle, duration: 0 },
  warning: { color: "var(--warning)", Icon: AlertTriangle, duration: 4000 },
  info: { color: "var(--info)", Icon: Info, duration: 4000 },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  }, []);

  const show = useCallback(
    (type, message, opts = {}) => {
      const id = Math.random().toString(36).slice(2);
      const duration = opts.duration ?? CONFIG[type].duration;
      let dup = false;
      setToasts((ts) => {
        // Same message already on screen: don't stack copies of it.
        if (ts.some((t) => t.type === type && t.message === message && !opts.action)) { dup = true; return ts; }
        return [...ts, { id, type, message, action: opts.action }].slice(-MAX);
      });
      if (dup) return id;
      if (duration > 0) timers.current[id] = setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  // Stable identity: consumers list `toast` in hook deps, so a new object per
  // render would re-run their effects on every toast (endless request loops).
  const api = useMemo(() => ({
    success: (m, o) => show("success", m, o),
    error: (m, o) => show("error", m, o),
    warning: (m, o) => show("warning", m, o),
    info: (m, o) => show("info", m, o),
    // Soft-delete undo pattern (feedback-system.md) is just an info toast + action.
    undo: (m, onUndo, o = {}) => show("info", m, { duration: 6000, action: { label: "Undo", onClick: onUndo }, ...o }),
    dismiss,
  }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        data-testid="toast-region"
        className="toast-region"
      >
        <AnimatePresence>
          {toasts.map((t) => {
            const { color, Icon } = CONFIG[t.type];
            const isError = t.type === "error";
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 40 }}
                transition={springs.card}
                role={isError ? "alert" : "status"}
                aria-live={isError ? "assertive" : "polite"}
                data-testid={`toast-${t.type}`}
                className="glass-card"
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", minWidth: 280, borderLeft: `4px solid ${color}`, borderRadius: 16, background: "var(--bg-surface)" }}
              >
                <Icon style={{ color, width: 20, height: 20, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 14, color: "var(--text-primary)" }}>{t.message}</span>
                {t.action && (
                  <button
                    onClick={() => { t.action.onClick(); dismiss(t.id); }}
                    data-testid="toast-action"
                    style={{ color: "var(--accent-primary)", fontWeight: 600, fontSize: 13 }}
                  >
                    {t.action.label}
                  </button>
                )}
                <button onClick={() => dismiss(t.id)} aria-label="Dismiss" style={{ color: "var(--text-secondary)" }}>
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
